// src/url.ts
function buildApiUrl(baseUrl, endpoint) {
  if (!baseUrl) return endpoint;
  return `${baseUrl.replace(/\/$/, "")}${endpoint}`;
}
function resolveApiBaseUrl(config) {
  const { baseUrl, isDev = false, devProxyForLocalhost = true, rawEnvUrl } = config;
  if (!devProxyForLocalhost || !isDev) {
    return baseUrl.replace(/\/$/, "");
  }
  const probe = (rawEnvUrl ?? baseUrl).replace(/\/$/, "");
  if (probe && (probe.includes("localhost") || probe.includes("127.0.0.1"))) {
    return "";
  }
  return baseUrl.replace(/\/$/, "");
}

// src/response-body.ts
function readIssueMessages(value) {
  if (!Array.isArray(value)) return [];
  const parts = [];
  for (const item of value) {
    if (typeof item === "string" && item.trim()) {
      parts.push(item.trim());
      continue;
    }
    if (item && typeof item === "object" && "message" in item) {
      const message = item.message;
      if (typeof message === "string" && message.trim()) {
        const path = item.path;
        const pathLabel = Array.isArray(path) && path.length > 0 ? `${path.join(".")}: ` : "";
        parts.push(`${pathLabel}${message.trim()}`);
      }
    }
  }
  return parts;
}
function isGenericValidationMessage(message) {
  return /^validation failed$/i.test(message.trim());
}
function extractApiErrorMessage(data) {
  if (typeof data === "string") {
    const trimmed = data.trim();
    if (!trimmed) return void 0;
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        return extractApiErrorMessage(JSON.parse(trimmed));
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }
  if (!data || typeof data !== "object") return void 0;
  const record = data;
  const nestedError = typeof record.error === "object" && record.error !== null ? record.error : null;
  const fromMessage = readStringMessage(record.message);
  const fromNested = nestedError ? readStringMessage(nestedError.message) : void 0;
  const fromErrors = readIssueMessages(record.errors);
  const fromIssues = readIssueMessages(record.issues);
  const details = fromErrors.length > 0 ? fromErrors : fromIssues.length > 0 ? fromIssues : nestedError ? readIssueMessages(nestedError.errors) : [];
  if (fromMessage && !isGenericValidationMessage(fromMessage)) {
    return fromMessage;
  }
  if (fromNested && !isGenericValidationMessage(fromNested)) {
    return fromNested;
  }
  if (details.length > 0) {
    return details.join(". ");
  }
  return fromMessage ?? fromNested;
}
function readStringMessage(value) {
  if (typeof value !== "string") return void 0;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : void 0;
}
async function readResponseBody(response) {
  const text = await response.text();
  if (!text.trim()) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// src/client.ts
var DEFAULT_REFRESH_ENDPOINT = "/api/v2/auth/refresh-token";
var SESSION_SUPERSEDED_ERROR_CODE = "SESSION_SUPERSEDED";
function parseAuthErrorCode(payload) {
  if (!payload || typeof payload !== "object") return void 0;
  const code = payload.code;
  return typeof code === "string" && code.length > 0 ? code : void 0;
}
function extractAuthErrorCodeFromThrown(error) {
  if (!error || typeof error !== "object") return void 0;
  return parseAuthErrorCode(error.data);
}
async function readAuthErrorCodeFromResponse(response) {
  const contentType = response.headers.get("content-type");
  if (!contentType?.includes("application/json")) return void 0;
  try {
    const data = await response.clone().json();
    return parseAuthErrorCode(data);
  } catch {
    return void 0;
  }
}
function isSessionSupersededErrorCode(code) {
  return code === SESSION_SUPERSEDED_ERROR_CODE;
}
function resolveEntryApp(entryApp) {
  return typeof entryApp === "function" ? entryApp() : entryApp;
}
function resolveExtraHeaders(extraHeaders) {
  if (!extraHeaders) return {};
  return typeof extraHeaders === "function" ? extraHeaders() : extraHeaders;
}
function createApiClient(options) {
  const {
    baseUrl,
    entryApp,
    tokenStorage,
    refreshTokenEndpoint = DEFAULT_REFRESH_ENDPOINT,
    refreshToken,
    isPublicAuthFailureEndpoint,
    onSessionInvalidated,
    onUnauthorizedJsonError,
    extraHeaders,
    credentials = "include",
    devDirectBaseUrl,
    refreshWithoutAccessToken = false
  } = options;
  let isRefreshing = false;
  let refreshPromise = null;
  let sessionInvalidated = false;
  async function getAccessToken() {
    const token = tokenStorage.getAccessToken();
    return token instanceof Promise ? token : Promise.resolve(token);
  }
  async function invalidateSession(ctx) {
    if (!onSessionInvalidated) return;
    const handled = await onSessionInvalidated(ctx);
    if (handled === false) return;
    sessionInvalidated = true;
  }
  async function runRefresh() {
    if (!refreshToken) return;
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = (async () => {
        try {
          await refreshToken();
        } finally {
          isRefreshing = false;
          refreshPromise = null;
        }
      })();
    }
    await refreshPromise;
  }
  async function refreshWithDirectFallback() {
    if (!refreshToken && !tokenStorage.saveAccessToken) return false;
    if (devDirectBaseUrl && baseUrl.startsWith("/")) {
      const urls = [
        buildApiUrl(baseUrl, refreshTokenEndpoint),
        buildApiUrl(devDirectBaseUrl, refreshTokenEndpoint)
      ];
      for (const url of urls) {
        try {
          const response = await fetch(url, {
            method: "POST",
            headers: {
              Accept: "application/json",
              "X-Imobdesk-Entry-App": resolveEntryApp(entryApp),
              ...resolveExtraHeaders(extraHeaders)
            },
            credentials
          });
          if (response.ok) {
            if (tokenStorage.saveAccessToken) {
              const text = await response.text();
              try {
                const body = JSON.parse(text);
                if (body.accessToken) {
                  await tokenStorage.saveAccessToken(body.accessToken);
                }
              } catch {
              }
            }
            return true;
          }
        } catch {
        }
      }
      return false;
    }
    await runRefresh();
    return true;
  }
  async function fetchWithOptionalDirectFallback(endpoint, requestOptions, directFallback = false) {
    const requestBaseUrl = directFallback && devDirectBaseUrl ? devDirectBaseUrl : baseUrl;
    const url = buildApiUrl(requestBaseUrl, endpoint);
    const headers = new Headers({
      Accept: "application/json",
      ...resolveExtraHeaders(extraHeaders)
    });
    if (requestOptions.headers) {
      const incoming = new Headers(requestOptions.headers);
      incoming.forEach((value, key) => {
        if (value) headers.set(key, value);
      });
    }
    if (requestOptions.body) {
      headers.set("Content-Type", "application/json");
    }
    const accessToken = await getAccessToken();
    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }
    headers.set("X-Imobdesk-Entry-App", resolveEntryApp(entryApp));
    try {
      return await fetch(url, {
        ...requestOptions,
        headers,
        credentials
      });
    } catch (networkError) {
      const canDirectRetry = !directFallback && !!devDirectBaseUrl && baseUrl.startsWith("/");
      if (canDirectRetry) {
        return fetchWithOptionalDirectFallback(endpoint, requestOptions, true);
      }
      throw networkError;
    }
  }
  async function apiClient(endpoint, requestOptions = {}, retryCount = 0, directFallback = false) {
    const accessToken = await getAccessToken();
    const response = await fetchWithOptionalDirectFallback(
      endpoint,
      requestOptions,
      directFallback
    );
    const isPublicAuth = isPublicAuthFailureEndpoint?.(endpoint) ?? false;
    if (response.status === 401 && endpoint !== refreshTokenEndpoint && !isPublicAuth) {
      if (sessionInvalidated) {
        return response;
      }
      const initialErrorCode = await readAuthErrorCodeFromResponse(response);
      const skipRefreshForSuperseded = isSessionSupersededErrorCode(
        initialErrorCode
      );
      const canTryRefresh = !skipRefreshForSuperseded && retryCount === 0 && (Boolean(accessToken) || refreshWithoutAccessToken);
      if (!canTryRefresh || !refreshToken && !devDirectBaseUrl) {
        await invalidateSession({
          endpoint,
          reason: skipRefreshForSuperseded ? "session_superseded" : "unauthorized_without_token",
          status: response.status,
          errorCode: initialErrorCode
        });
        return response;
      }
      try {
        if (devDirectBaseUrl) {
          const ok = await refreshWithDirectFallback();
          if (!ok) throw new Error("refresh failed");
        } else {
          await runRefresh();
        }
      } catch (refreshError) {
        const refreshErrorCode = extractAuthErrorCodeFromThrown(refreshError) ?? initialErrorCode;
        await invalidateSession({
          endpoint,
          reason: isSessionSupersededErrorCode(refreshErrorCode) ? "session_superseded" : "refresh_failed",
          status: response.status,
          errorCode: refreshErrorCode
        });
        return response;
      }
      return apiClient(endpoint, requestOptions, retryCount + 1, directFallback);
    }
    return response;
  }
  async function apiClientJson(endpoint, requestOptions = {}) {
    const response = await apiClient(endpoint, requestOptions);
    const data = await readResponseBody(response);
    if (!response.ok) {
      const isPublicAuth = isPublicAuthFailureEndpoint?.(endpoint) ?? false;
      if (response.status === 401 && endpoint !== refreshTokenEndpoint && !isPublicAuth) {
        const errorCode = parseAuthErrorCode(data);
        await onUnauthorizedJsonError?.(endpoint, { errorCode });
        if (!sessionInvalidated) {
          await invalidateSession({
            endpoint,
            reason: isSessionSupersededErrorCode(errorCode) ? "session_superseded" : "unauthorized_after_response",
            status: response.status,
            errorCode
          });
        }
      }
      const apiMessage = extractApiErrorMessage(data);
      throw {
        status: response.status,
        statusText: response.statusText,
        data,
        ...apiMessage ? { message: apiMessage } : {}
      };
    }
    if (data === null || typeof data === "string") {
      throw new Error(
        typeof data === "string" ? `Resposta n\xE3o \xE9 JSON: ${data}` : "Resposta vazia da API"
      );
    }
    return data;
  }
  async function apiClientJsonParsed(endpoint, schema, requestOptions = {}) {
    const data = await apiClientJson(endpoint, requestOptions);
    return schema.parse(data);
  }
  return {
    apiClient,
    apiClientJson,
    apiClientJsonParsed
  };
}

// src/server-client.ts
var ApiClientError = class extends Error {
  status;
  payload;
  constructor(message, status, payload) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.payload = payload;
  }
};
function resolveValue(value) {
  return typeof value === "function" ? value() : value;
}
function resolveExtraHeaders2(extraHeaders) {
  if (!extraHeaders) return {};
  return typeof extraHeaders === "function" ? extraHeaders() : extraHeaders;
}
function buildUrl(baseUrl, path, query) {
  const url = new URL(
    buildApiUrl(baseUrl, path.startsWith("/") ? path : `/${path}`)
  );
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === void 0 || value === null) continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}
async function handleResponse(response) {
  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const body = isJson ? await response.json().catch(() => null) : await response.text();
  if (!response.ok) {
    const message = isJson && body?.message || typeof body === "string" && body || `Erro HTTP ${response.status} ao chamar a API`;
    throw new ApiClientError(
      typeof message === "string" ? message : "Erro desconhecido",
      response.status,
      body
    );
  }
  return body;
}
function createServerApiClient(options) {
  const {
    baseUrl,
    entryApp,
    internalAuthToken,
    cache = "no-store",
    extraHeaders
  } = options;
  function buildHeaders(opts, withBody = false) {
    const headers = {
      Accept: "application/json",
      "X-Imobdesk-Entry-App": entryApp,
      ...resolveExtraHeaders2(extraHeaders)
    };
    if (withBody) {
      headers["Content-Type"] = "application/json";
    }
    if (opts?.bearer) {
      headers.Authorization = `Bearer ${opts.bearer}`;
    }
    if (opts?.internal && internalAuthToken) {
      headers["X-Internal-Auth"] = resolveValue(internalAuthToken);
    }
    return headers;
  }
  async function request(path, init, opts) {
    const response = await fetch(buildUrl(resolveValue(baseUrl), path, opts?.query), {
      ...init,
      headers: buildHeaders(opts, init.body !== void 0),
      signal: opts?.signal,
      cache
    });
    return handleResponse(response);
  }
  return {
    get(path, opts) {
      return request(path, { method: "GET" }, opts);
    },
    getParsed(path, schema, opts) {
      return request(path, { method: "GET" }, opts).then(
        (data) => schema.parse(data)
      );
    },
    post(path, body, opts) {
      return request(
        path,
        {
          method: "POST",
          body: body !== void 0 ? JSON.stringify(body) : void 0
        },
        opts
      );
    },
    patch(path, body, opts) {
      return request(
        path,
        {
          method: "PATCH",
          body: body !== void 0 ? JSON.stringify(body) : void 0
        },
        opts
      );
    },
    delete(path, opts) {
      return request(path, { method: "DELETE" }, opts);
    }
  };
}

// src/parse.ts
function parseWith(schema, data) {
  return schema.parse(data);
}

export { ApiClientError, SESSION_SUPERSEDED_ERROR_CODE, buildApiUrl, createApiClient, createServerApiClient, extractApiErrorMessage, parseAuthErrorCode, parseWith, readResponseBody, resolveApiBaseUrl };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map